import React, { useState, useRef, useEffect } from "react";
import { cn } from "../../../lib/utils";
import { Send, Bot, User, Loader2, Server } from "lucide-react";
import { Button } from "../../../ui/button";
import { ChatMessage } from "../interfaces/gatewayInterfaces";
import "../AgenticGateway.css";

interface GatewayChatPanelProps {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  loading?: boolean;
  customRenderer?: (content: string) => React.ReactNode | null;
  suggestions?: string[];
  placeholder?: string;
  welcomeMessage?: React.ReactNode;
  className?: string;
  initialInput?: string;
}


function CapabilityPills({ label, items, maxVisible = 12 }: { label: string; items: string[]; maxVisible?: number }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, maxVisible);
  const remaining = items.length - maxVisible;

  return (
    <div className="mt-4">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Available {label} ({items.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {visible.map((item, j) => (
          <span
            key={j}
            className="inline-flex items-center px-2.5 py-1 rounded-full border border-foreground/20 ag-surface text-foreground/80 text-[11px] font-mono shadow-sm"
          >
            {item}
          </span>
        ))}
        {!showAll && remaining > 0 && (
          <button
            onClick={() => setShowAll(true)}
            className="inline-flex items-center px-2.5 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-[11px] font-medium hover:bg-primary/15 transition-colors"
          >
            + {remaining} more
          </button>
        )}
        {showAll && items.length > maxVisible && (
          <button
            onClick={() => setShowAll(false)}
            className="inline-flex items-center px-2.5 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-[11px] font-medium hover:bg-primary/15 transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

function ServerCapCard({ name, capabilities }: { name: string; capabilities: { label: string; items: string[] }[] }) {
  const toolsCap = capabilities.find((c) => c.label.toUpperCase() === "TOOLS");
  const promptsCap = capabilities.find((c) => c.label.toUpperCase() === "PROMPTS");
  const resourcesCap = capabilities.find((c) => c.label.toUpperCase() === "RESOURCES");

  const hasTools = toolsCap && toolsCap.items.length > 0 && toolsCap.items[0] !== "None listed";
  const hasPrompts = promptsCap && promptsCap.items.length > 0 && promptsCap.items[0] !== "None listed";
  const hasResources = resourcesCap && resourcesCap.items.length > 0 && resourcesCap.items[0] !== "None listed";

  return (
    <div className="ag-card p-4 my-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg ag-surface border border-border flex items-center justify-center">
            <Server className="w-4 h-4 text-muted-foreground" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">{name}</div>
          </div>
        </div>
      </div>

      {hasTools && <CapabilityPills label="Tools" items={toolsCap!.items} />}
      {hasPrompts && <CapabilityPills label="Prompts" items={promptsCap!.items} />}
      {hasResources && <CapabilityPills label="Resources" items={resourcesCap!.items} />}
    </div>
  );
}

function formatContent(text: string) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let tableLines: string[] = [];
  let inTable = false;

  function flushTable() {
    if (tableLines.length === 0) return;
    const rows = tableLines.filter((l) => !l.match(/^\|[\s-:|]+\|$/));
    const cells = rows.map((r) =>
      r.split("|").filter(Boolean).map((c) => c.trim())
    );
    elements.push(
      <div key={`table-${elements.length}`} className="overflow-x-auto my-2">
        <table className="text-xs border-collapse w-full">
          {cells.length > 0 && (
            <thead>
              <tr>
                {cells[0].map((c, i) => (
                  <th
                    key={i}
                    className="border border-border ag-surface px-2 py-1 text-left font-semibold text-foreground/70"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {cells.slice(1).map((row, ri) => (
              <tr key={ri}>
                {row.map((c, ci) => (
                  <td
                    key={ci}
                    className="border border-border px-2 py-1 text-foreground/80"
                  >
                    {c.replace(/`/g, "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableLines = [];
    inTable = false;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table row
    if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
      inTable = true;
      tableLines.push(line.trim());
      continue;
    }

    if (inTable) flushTable();

    // Heading ### (server name sub-heading) — render as a mini card header
    if (line.match(/^###\s+/)) {
      const serverName = line.replace(/^###\s+/, "").trim();
      // Look ahead to collect capabilities for this server
      const caps: { label: string; items: string[] }[] = [];
      while (i + 1 < lines.length) {
        const next = lines[i + 1];
        const capMatch = next.match(/^\s*[-•]\s+\*\*(\w+)\*\*:\s*(.+)$/);
        if (capMatch) {
          caps.push({
            label: capMatch[1],
            items: capMatch[2].split(",").map((s) => s.trim()).filter(Boolean),
          });
          i++;
        } else {
          break;
        }
      }
      elements.push(
        <ServerCapCard key={`srv-${elements.length}`} name={serverName} capabilities={caps} />
      );
      continue;
    }

    // Heading ##
    if (line.match(/^##\s+/)) {
      elements.push(
        <div
          key={i}
          className="text-sm font-semibold text-foreground mt-3 mb-1 border-b border-border pb-1"
        >
          {line.replace(/^##\s+/, "")}
        </div>
      );
      continue;
    }

    // Standalone bullet with label: "- **LABEL**: item1, item2, ..."
    if (line.match(/^\s*[-•]\s+\*\*\w+\*\*:\s*.+,/)) {
      const match = line.match(/^\s*[-•]\s+\*\*(\w+)\*\*:\s*(.+)$/);
      if (match) {
        const label = match[1];
        const items = match[2].split(",").map((s) => s.trim()).filter(Boolean);
        elements.push(
          <CapabilityPills key={`cap-${elements.length}`} label={label} items={items} />
        );
        continue;
      }
    }

    // Regular bullet
    if (line.match(/^\s*[-•]\s+/)) {
      const content = line.replace(/^\s*[-•]\s+/, "");
      elements.push(
        <div key={i} className="flex gap-1.5 ml-2">
          <span className="text-muted-foreground/50 mt-0.5 text-[8px]">●</span>
          <span>{formatInline(content)}</span>
        </div>
      );
      continue;
    }

    // Empty line
    if (!line.trim()) {
      elements.push(<div key={i} className="h-2" />);
      continue;
    }

    // Normal text
    elements.push(
      <div key={i}>{formatInline(line)}</div>
    );
  }

  if (inTable) flushTable();

  return elements;
}

/** Format bold, code, and inline markdown */
function formatInline(text: string): React.ReactNode {
  // Split by **bold** and `code`
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={i} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </span>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={i}
          className="px-1 py-0.5 rounded ag-surface text-foreground/90 font-mono text-[11px]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

export default function GatewayChatPanel({
  messages, onSend, loading = false, suggestions, placeholder = "Describe your requirements...",
  welcomeMessage, className, customRenderer, initialInput,
}: GatewayChatPanelProps) {
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Seed the input from a caller (e.g. a CISO priority action hands a drafted brief via ?seed=).
  useEffect(() => {
    if (initialInput) setInput(initialInput);
  }, [initialInput]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    onSend(trimmed);
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  return (
    <div className={cn("flex flex-col", className)} style={{ minHeight: 0 }}>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{ minHeight: 0 }}
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 space-y-5">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-primary" />
            </div>

            {welcomeMessage && (
              <div className="text-center max-w-sm">
                <p className="text-sm font-medium text-foreground mb-1">How can I help?</p>
                <div className="text-xs text-muted-foreground leading-relaxed">{welcomeMessage}</div>
              </div>
            )}

            {suggestions && suggestions.length > 0 && (
              <div className="grid grid-cols-2 gap-2 w-full max-w-md">
                {suggestions.map((s, i) => {
                  const isOdd = suggestions.length % 2 !== 0 && i === suggestions.length - 1;
                  return (
                    <button
                      key={i}
                      onClick={() => onSend(s)}
                      className={cn(
                        "text-left px-3 py-2.5 ag-card portal-card-hover text-xs text-muted-foreground hover:text-foreground group",
                        isOdd && "col-span-2"
                      )}
                    >
                      <span className="text-primary/60 group-hover:text-primary mr-1">→</span>
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn("flex gap-3", msg.role === "user" && "flex-row-reverse")}>
            <div className={cn(
              "shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5",
              msg.role === "user" ? "bg-blue-500/20" : "bg-primary/20"
            )}>
              {msg.role === "user" ? <User className="w-4 h-4 text-blue-600 dark:text-blue-400" /> : <Bot className="w-4 h-4 text-primary" />}
            </div>
            <div className={cn(
              "text-sm leading-relaxed max-w-[85%] rounded-lg px-3 py-2",
              msg.role === "user"
                ? "bg-blue-500/10 text-foreground"
                : "ag-surface text-foreground/80"
            )}>
              {msg.role === "user"
                ? msg.content
                : (customRenderer?.(msg.content) ?? formatContent(msg.content))}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="shrink-0 w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
            </div>
            <div className="text-sm text-muted-foreground">Thinking...</div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="border-t border-border p-3 shrink-0">
        <div className="flex gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className="flex-1 bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground resize-none outline-none focus:border-primary/50 transition-colors"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)] shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
